/**
 * Page-aware AI actions — the bridge between "where the user is" and "what the
 * Agente can DO here". The ⌘K drawer renders these as function buttons (verb +
 * kind), so every page offers its own AI operations instead of loose prompt
 * chips scattered around the UI.
 *
 * Two modes per action:
 *  - `send`: a complete instruction; fires immediately (writes still go through
 *    the assistant's propose→confirm gate, so nothing executes un-reviewed).
 *  - `draft`: an instruction missing a user-supplied piece (a name, a value);
 *    it seeds the composer for completion instead of sending.
 *
 * Every prompt is backed by a real assistant tool (see the demo router and
 * `ai/tool-catalog.ts`) — no decorative prompts.
 */

export interface PageAiAction {
  /** Short verb-first label, es-PA (what the function DOES). */
  label: string;
  /** The instruction sent (send) or seeded (draft). */
  prompt: string;
  /** consulta = read tool · accion = write tool (propose→confirm). */
  kind: 'consulta' | 'accion';
  /** send = fire now · draft = seed the composer for completion. */
  mode: 'send' | 'draft';
}

const DETAIL: PageAiAction[] = [
  { label: 'Explicar el riesgo', prompt: 'Explícame el riesgo de este cliente', kind: 'consulta', mode: 'send' },
  { label: 'Documentos pendientes', prompt: '¿Qué documentos le faltan?', kind: 'consulta', mode: 'send' },
  { label: 'Correr screening', prompt: 'Ejecuta la validación de sanciones', kind: 'accion', mode: 'send' },
];

const CLIENTES: PageAiAction[] = [
  { label: 'Ver clientes recientes', prompt: 'Muéstrame los clientes recientes', kind: 'consulta', mode: 'send' },
  { label: 'Buscar un cliente', prompt: 'Busca a ', kind: 'consulta', mode: 'draft' },
  { label: 'Registrar cliente con IA', prompt: 'Crea un expediente para ', kind: 'accion', mode: 'draft' },
];

const REGISTRY: { match: (p: string) => boolean; actions: PageAiAction[] }[] = [
  {
    match: (p) => p.startsWith('/matriz-riesgo'),
    actions: [
      { label: 'Explicar la matriz', prompt: '¿Cómo está configurada la matriz de riesgo y de dónde salen los pesos?', kind: 'consulta', mode: 'send' },
      { label: 'Umbrales y DDC', prompt: '¿Qué significan los umbrales y la DDC?', kind: 'consulta', mode: 'send' },
      { label: 'Re-calificar un país', prompt: 'Pon Venezuela en alto.', kind: 'accion', mode: 'draft' },
      { label: 'Cambiar un umbral', prompt: 'Cambia el umbral alto a 75.', kind: 'accion', mode: 'draft' },
      { label: 'Cambiar tipo de entidad', prompt: 'Cambia el tipo de entidad a seguros.', kind: 'accion', mode: 'draft' },
    ],
  },
  {
    match: (p) => /^\/(expedientes|revision)\/[^/]+$/.test(p) && !p.endsWith('/nuevo'),
    actions: DETAIL,
  },
  { match: (p) => p === '/expedientes' || p === '/inicio', actions: CLIENTES },
  {
    match: (p) => p.startsWith('/alertas'),
    actions: [
      { label: 'Resumen de alertas', prompt: 'Muéstrame las alertas recientes', kind: 'consulta', mode: 'send' },
      { label: 'Asignar una alerta', prompt: 'Asigna la alerta de ', kind: 'accion', mode: 'draft' },
      { label: 'Descartar una alerta', prompt: 'Descarta la alerta de ', kind: 'accion', mode: 'draft' },
    ],
  },
  {
    match: (p) => p === '/panel',
    actions: [
      { label: 'Resumen de la cartera', prompt: 'Dame un resumen de cómo vamos', kind: 'consulta', mode: 'send' },
      { label: 'Alertas abiertas', prompt: 'Muéstrame las alertas recientes', kind: 'consulta', mode: 'send' },
      { label: 'Clientes recientes', prompt: 'Muéstrame los clientes recientes', kind: 'consulta', mode: 'send' },
    ],
  },
  {
    match: (p) => p === '/validacion-masiva',
    actions: [
      { label: 'Screening de un nombre', prompt: 'Screening de ', kind: 'consulta', mode: 'draft' },
      { label: 'Ver cartera', prompt: 'Muéstrame los clientes recientes', kind: 'consulta', mode: 'send' },
    ],
  },
  {
    match: (p) => p === '/alcance',
    actions: [
      { label: 'Solicitar documentos', prompt: 'Solicita documentos a ', kind: 'accion', mode: 'draft' },
      { label: 'Documentos de un cliente', prompt: '¿Qué documentos le faltan a ', kind: 'consulta', mode: 'draft' },
    ],
  },
];

/** Fallback for pages with no specific mapping — the two universal reads. */
const DEFAULT_ACTIONS: PageAiAction[] = [
  { label: 'Resumen de la cartera', prompt: 'Dame un resumen de cómo vamos', kind: 'consulta', mode: 'send' },
  { label: 'Ver clientes recientes', prompt: 'Muéstrame los clientes recientes', kind: 'consulta', mode: 'send' },
];

/** Actions for the page at `pathname`. The /agente page renders its own catalog. */
export function pageAiActions(pathname: string): PageAiAction[] {
  if (pathname.startsWith('/agente')) return [];
  return REGISTRY.find((r) => r.match(pathname))?.actions ?? DEFAULT_ACTIONS;
}

/** Open the ⌘K assistant drawer from anywhere (AppHeader listens for this). */
export function openAssistant(): void {
  window.dispatchEvent(new CustomEvent('cumpli:open-assistant'));
}
